import { cn } from '@/shared/lib/cn';

import { IconChip } from './IconChip';
import type { IconName } from './Icon';
import { Skeleton } from './Skeleton';
import { glowTone, highlightTone, textTone, type ToneName } from './tones';

/**
 * Indicador do painel.
 *
 * Zero e um valor legitimo e aparece como zero. Um painel que esconde o zero ou o
 * troca por um numero de exemplo ensina a nao confiar nele.
 *
 * O tom colore o icone pelo sentido do indicador (`tones.ts`). O destaque e para o
 * indicador que pede acao agora: o cartao ganha o veu e a faixa do tom, e o numero a
 * cor dele. Sem destaque, o cartao fica como os outros.
 */
export function KpiCard({
  label,
  value,
  hint,
  icon,
  tone = 'brand',
  highlight = false,
  loading,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  icon: IconName;
  tone?: ToneName | undefined;
  highlight?: boolean | undefined;
  loading?: boolean | undefined;
}) {
  const destacado = highlight && !loading;
  return (
    <article
      className={cn(
        'surface-card group relative flex items-start gap-3.5 overflow-hidden p-4',
        'transition-[box-shadow,border-color] duration-(--duration-base) ease-(--ease-out-soft)',
        'hover:shadow-(--shadow-md)',
        destacado ? 'bg-linear-to-br to-surface to-70%' : glowTone[tone],
        destacado && 'before:absolute before:inset-y-0 before:left-0 before:w-1',
        destacado && highlightTone[tone],
      )}
    >
      <IconChip
        icon={icon}
        tone={tone}
        className="group-hover:scale-105 motion-reduce:group-hover:scale-100"
      />

      <div className="min-w-0 flex-1">
        <p className="text-xs leading-snug font-medium text-ink-600">{label}</p>
        {loading ? (
          <Skeleton className="mt-1.5 h-7 w-20" />
        ) : (
          <p
            className={cn(
              'mt-1 truncate text-2xl leading-tight font-semibold tracking-tight tabular-nums',
              destacado ? textTone[tone] : 'text-ink-900',
            )}
          >
            {value}
          </p>
        )}
        {hint ? <p className="mt-1 truncate text-2xs text-ink-500">{hint}</p> : null}
      </div>
    </article>
  );
}
